import { Components, styled, Theme } from '@mui/material'

declare module '@mui/material/Checkbox' {
  interface CheckboxProps {
    'data-is-ai-prefilled'?: boolean
    // Paints the checked box in a caller-supplied colour, for a checkbox that identifies something already drawn in
    // that colour (an Insights chart series). Any other use wants the default brand box.
    'data-swatch-colour'?: string
  }
}

const iconClassName = 'checkbox-icon'

// From the Checkbox Multicolour spec: the hues whose box is light enough to need a deep tick rather than a pale
// one. The set differs per theme — coral, peach, orange and teal are pale enough to flip only once the box is
// composited on a dark surface — and it is an explicit list rather than a contrast calculation because design
// picked the hues one by one (peach keeps a pale tick in light theme despite being lighter than mint).
const DEEP_TICK_HUES = {
  light: ['mint', 'sky', 'yellow'],
  dark: ['coral', 'peach', 'orange', 'teal', 'mint', 'sky', 'yellow'],
} as const satisfies Record<'light' | 'dark', readonly (keyof Theme['palette']['dataViz'])[]>

const tickColourFor = (swatchColour: string, theme: Theme): string => {
  // fg.default and fg.inverted are exact opposites in each theme, so the pale and deep ticks are the same
  // two tokens swapped rather than four separate ones.
  const isDark = theme.palette.mode === 'dark'
  const pale = isDark ? theme.palette.fg.default : theme.palette.fg.inverted
  const deep = isDark ? theme.palette.fg.inverted : theme.palette.fg.default
  const needsDeepTick = DEEP_TICK_HUES[isDark ? 'dark' : 'light'].some(
    (hue) => swatchColour === theme.palette.dataViz[hue].default || swatchColour === theme.palette.dataViz[hue].hover
  )
  return needsDeepTick ? deep : pale
}

// Hover state for a given checkbox state, including label hover when the
// checkbox is wrapped in a FormControlLabel.
const hoverSelector = (stateClass = '') =>
  `&${stateClass}:hover:not(.Mui-disabled) input ~ .${iconClassName}, label:hover &${stateClass}:not(.Mui-disabled) input ~ .${iconClassName}, .MuiMenuItem-root:hover &${stateClass}:not(.Mui-disabled) input ~ .${iconClassName}`

const BaseIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  borderRadius: theme.shape['radius-sm'],
  borderStyle: 'solid',
  borderWidth: theme.strokeWidth.strong,
  width: '18px',
  height: '18px',
}))

const CheckedIcon = () => (
  <BaseIcon className={iconClassName}>
    <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.74647 0.37107C10.094 -0.05819 10.7244 -0.124864 11.1537 0.222633C11.5828 0.570142 11.6486 1.19967 11.3012 1.62888L4.82557 9.62888C4.63511 9.86416 4.34799 10.0008 4.0453 9.99998C3.74258 9.9991 3.45612 9.86133 3.26698 9.62498L0.219124 5.81541C-0.125859 5.38415 -0.0558781 4.75416 0.375374 4.40916C0.806626 4.06418 1.43662 4.13418 1.78162 4.56541L4.05213 7.40427L9.74647 0.37107Z"
        fill="currentColor"
      />
    </svg>
  </BaseIcon>
)
const IndeterminateIcon = () => (
  <BaseIcon className={iconClassName}>
    <svg width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="0.75" y1="0.75" x2="9.25" y2="0.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  </BaseIcon>
)

export const MuiCheckbox: Components<Omit<Theme, 'components'>>['MuiCheckbox'] = {
  defaultProps: {
    disableRipple: true,
    icon: <BaseIcon className={iconClassName} />,
    checkedIcon: <CheckedIcon />,
    indeterminateIcon: <IndeterminateIcon />,
  },
  styleOverrides: {
    root: ({ theme, 'data-is-ai-prefilled': isAiPrefilled, 'data-swatch-colour': swatchColour }) => {
      const tick = swatchColour ? tickColourFor(swatchColour, theme) : theme.palette.fg.brand.onBrand
      const checkedIcon = (brandBackground: string) => ({
        backgroundColor: swatchColour ?? brandBackground,
        borderColor: swatchColour ?? brandBackground,
        color: tick,
      })

      return {
        padding: theme.spacing(2),
        '.MuiListItemIcon-root &': {
          padding: 0,
        },

        '& ~ .MuiFormControlLabel-label': {
          // Combination of padding 2 above and margin 1 here gives spacing 3 between the visual part of the checkbox and the label
          marginLeft: theme.spacing(1),
        },

        '&.Mui-disabled': {
          opacity: 0.3,
        },

        [`& .${iconClassName}`]: {
          borderColor: theme.palette.border.strong,
        },

        [`&.Mui-checked .${iconClassName}`]: checkedIcon(theme.palette.bg.brand.default),
        [`&.MuiCheckbox-indeterminate .${iconClassName}`]: {
          color: theme.palette.border.strong,
        },

        // Hover state for unchecked (inc indeterminate) checkboxes
        [hoverSelector()]: {
          borderColor: theme.palette.border.strongest,
          color: theme.palette.border.strongest,
        },
        // Hover state for checked checkboxes
        [hoverSelector('.Mui-checked')]: checkedIcon(theme.palette.bg.brand.hover),
        ...(isAiPrefilled && {
          [`&:not(.Mui-disabled) .${iconClassName}`]: {
            borderColor: theme.palette.bg.ai.default,
          },
          [`&.Mui-checked:not(.Mui-disabled) .${iconClassName}`]: {
            backgroundColor: theme.palette.bg.ai.default,
          },
          [`&.MuiCheckbox-indeterminate:not(.Mui-disabled) .${iconClassName}`]: {
            color: theme.palette.fg.ai.default,
          },
          [hoverSelector()]: {
            borderColor: theme.palette.bg.ai.strongest,
          },
          [hoverSelector('.Mui-checked')]: {
            backgroundColor: theme.palette.bg.ai.strongest,
          },
          [hoverSelector('.MuiCheckbox-indeterminate')]: {
            color: theme.palette.fg.ai.strongest,
          },
        }),
      }
    },
  },
}
